import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Logo from "../components/Logo";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { formatApiError } from "../lib/api";

export default function Auth() {
  const [params, setParams] = useSearchParams();
  const initialMode = params.get("mode") === "login" ? "login" : "register";
  const [mode, setMode] = useState(initialMode);
  const { login, register, user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate("/home", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password);
      } else {
        await register(email, password, name || email.split("@")[0]);
      }
      navigate("/home", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const switchMode = (next) => {
    setMode(next);
    setError("");
    setParams({ mode: next });
  };

  return (
    <div className="app-shell px-6 pt-6 pb-10">
      <div className="flex items-center justify-between">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-brand-brown/70 hover:text-brand-brown text-xs tracking-[0.25em] uppercase font-bold"
          data-testid="auth-back"
        >
          <ArrowLeft size={14} strokeWidth={1.75} /> ARISTEIA
        </Link>
        <LanguageSwitcher />
      </div>

      <div className="mt-16">
        <p className="text-[10px] tracking-[0.45em] uppercase font-bold text-brand-tan">
          {t("welcome_kicker")}
        </p>
        <h1
          className="font-serif text-5xl mt-3 text-brand-brown leading-none"
          data-testid="auth-heading"
        >
          {mode === "login" ? t("login") : t("register")}
        </h1>
      </div>

      <form
        onSubmit={submit}
        className="mt-10 space-y-5"
        data-testid="auth-form"
      >
        {mode === "register" && (
          <Field
            label={t("name")}
            value={name}
            onChange={setName}
            type="text"
            autoComplete="name"
            testid="auth-name"
          />
        )}
        <Field
          label={t("email")}
          value={email}
          onChange={setEmail}
          type="email"
          autoComplete="email"
          testid="auth-email"
          required
        />
        <Field
          label={t("password")}
          value={password}
          onChange={setPassword}
          type="password"
          autoComplete={mode === "login" ? "current-password" : "new-password"}
          testid="auth-password"
          required
        />

        {error && (
          <p
            className="text-sm text-destructive font-medium"
            data-testid="auth-error"
          >
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={busy}
          data-testid="auth-submit"
          className="w-full py-4 rounded-2xl bg-brand-brown text-brand-cream font-bold tracking-[0.18em] uppercase text-sm transition-transform active:scale-[0.98] disabled:opacity-60"
        >
          {busy ? "…" : mode === "login" ? t("login") : t("register")}
        </button>
      </form>

      <div className="mt-10 text-center">
        {mode === "login" ? (
          <button
            type="button"
            onClick={() => switchMode("register")}
            data-testid="switch-to-register"
            className="text-sm text-brand-brown/70 hover:text-brand-brown"
          >
            <span className="opacity-70">— </span>
            {t("register")}
            <span className="opacity-70"> —</span>
          </button>
        ) : (
          <button
            type="button"
            onClick={() => switchMode("login")}
            data-testid="switch-to-login"
            className="text-sm text-brand-brown/70 hover:text-brand-brown"
          >
            <span className="opacity-70">— </span>
            {t("cta_login")}
            <span className="opacity-70"> —</span>
          </button>
        )}
      </div>

      <div className="mt-20 text-center">
        <Logo tone="brown" size="sm" />
        <p className="text-[10px] tracking-[0.4em] uppercase text-brand-tan mt-2">
          {t("brand_tagline")}
        </p>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, required, autoComplete }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-[0.3em] uppercase font-bold text-brand-tan">
        {label}
      </span>
      <input
        type={type}
        autoComplete={autoComplete}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-2 w-full bg-transparent border-b border-brand-tan/40 focus:border-brand-brown py-2.5 outline-none text-brand-brown font-medium text-lg transition-colors"
      />
    </label>
  );
}
